/**
 * BudgetEditModal — create/edit budżetu.
 * Pola: kategoria (expense), kwota, okres (monthly/weekly/yearly), alertAt %.
 */
import { useState, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { Icons } from '../Icons';
import SegmentedControl from '../ui/SegmentedControl';
import { CategoryPicker } from '../transactions/CategoryPicker';
import { type Budget } from '../../features/budgets/budgetsStore';
import { createBudget, updateBudget, deleteBudget } from '../../features/budgets/api';
import { useAuthStore } from '../../features/auth/authStore';
import { useCategoriesStore } from '../../features/categories/categoriesStore';
import { parseAmountInput, formatAmountForInput, haptic } from '../../lib/utils';
import { showToast } from '../ui/Toast';

interface Props {
  budget: Budget | null;
  onClose: () => void;
}

export function BudgetEditModal({ budget, onClose }: Props) {
  const family = useAuthStore((s) => s.family);
  const categories = useCategoriesStore((s) => s.categories);
  const expenseCats = useMemo(() => categories.filter((c) => c.type === 'expense'), [categories]);

  const isEdit = !!budget;
  const [categoryId, setCategoryId] = useState<string | null>(budget?.categoryId ?? expenseCats[0]?.id ?? null);
  const [amount, setAmount] = useState(budget ? formatAmountForInput(budget.amount) : '');
  const [period, setPeriod] = useState<'monthly' | 'weekly' | 'yearly'>(budget?.period ?? 'monthly');
  const [alertAt, setAlertAt] = useState(String(budget?.alertAt ?? 80));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!categoryId) {
      setError('Wybierz kategorię');
      return;
    }
    const grosze = parseAmountInput(amount);
    if (!grosze) {
      setError('Podaj kwotę');
      return;
    }
    if (!family) return;
    const alertN = Math.max(50, Math.min(100, Number(alertAt) || 80));
    setSubmitting(true);
    try {
      if (isEdit && budget) {
        await updateBudget(budget.id, { categoryId, amount: grosze, period, alertAt: alertN });
        showToast('Zaktualizowano budżet', 'success');
      } else {
        await createBudget({
          familyId: family.id,
          walletId: 'all',
          categoryId,
          amount: grosze,
          period,
          alertAt: alertN,
        });
        showToast('Budżet utworzony', 'success');
      }
      haptic('success');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się zapisać');
      haptic('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!budget) return;
    if (!window.confirm('Usunąć budżet?')) return;
    setSubmitting(true);
    try {
      await deleteBudget(budget.id);
      showToast('Budżet usunięty', 'info');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się usunąć');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      size="md"
      icon={
        <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-500/15 ring-8 ring-amber-500/20 dark:ring-amber-400/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
          <Icons.ChartPie size={28} strokeWidth={2.25} />
        </div>
      }
      title={isEdit ? 'Edycja budżetu' : 'Nowy budżet'}
      footer={
        <div className="flex gap-2">
          {isEdit && (
            <button type="button" onClick={handleDelete} disabled={submitting} className="px-3 py-3 rounded-xl border border-rose-200 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 text-sm font-semibold hover:bg-rose-50 dark:hover:bg-rose-500/10 transition disabled:opacity-50">
              <Icons.Trash size={16} />
            </button>
          )}
          <button type="button" onClick={onClose} disabled={submitting} className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50">Anuluj</button>
          <button type="button" onClick={handleSave} disabled={submitting} className="flex-[1.5] px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card transition disabled:opacity-50">{submitting ? 'Zapisuję...' : isEdit ? 'Zapisz' : 'Utwórz'}</button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Kategoria</p>
          <CategoryPicker
            categories={categories}
            type="expense"
            value={categoryId}
            onChange={setCategoryId}
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Kwota miesięczna</label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="500,00"
              className="w-full text-2xl font-bold tabular-nums px-4 py-3 pr-14 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-base font-semibold text-slate-400">zł</span>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Okres</p>
          <SegmentedControl<'monthly' | 'weekly' | 'yearly'>
            options={[
              { key: 'weekly', label: 'Tyg.' },
              { key: 'monthly', label: 'Mies.' },
              { key: 'yearly', label: 'Rok' },
            ]}
            value={period}
            onChange={setPeriod}
            fullWidth
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Alert przy %</label>
          <input
            type="number"
            min={50}
            max={100}
            value={alertAt}
            onChange={(e) => setAlertAt(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 text-sm"
          />
          <p className="text-[11px] text-slate-400 mt-1">Pokażemy ostrzeżenie gdy wydatki w kategorii osiągną ten %.</p>
        </div>

        {error && (
          <div className="px-3 py-2.5 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-sm text-rose-700 dark:text-rose-300">{error}</div>
        )}
      </div>
    </Modal>
  );
}
